/**
 * Modal de pré-validation avant génération des emplois du temps.
 * Affiche l'analyse de capacité, alertes détaillées et propose des solutions.
 */

import React, { useState } from 'react';
import {
  AlertTriangle, CheckCircle, Calendar, ArrowRight, X, Loader2,
  ArrowLeftRight, Check, Settings, UserX, Clock, Info, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { PreValidationResult, JourEmpruntable, ContrainteEmploiTemps } from '../../services/emploisTempsApi';

interface Props {
  validation: PreValidationResult | null;
  isLoading: boolean;
  contraintes?: ContrainteEmploiTemps;
  dateDebut?: string;
  dateFin?: string;
  onGenerate: (joursEmpruntes?: { date: string; source: string; cible: string }[]) => void;
  onExtendPeriod: (newDateFin: string) => void;
  onApplyContraintes?: (payload: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

const PreValidationModal: React.FC<Props> = ({
  validation, isLoading, contraintes, dateDebut, dateFin, onGenerate, onExtendPeriod, onApplyContraintes, onClose,
}) => {
  const [applyingPayload, setApplyingPayload] = useState<Record<string, unknown> | null>(null);
  const handleApply = async (payload: Record<string, unknown>) => {
    if (!onApplyContraintes) return;
    setApplyingPayload(payload);
    try {
      await onApplyContraintes(payload);
    } finally {
      setApplyingPayload(null);
    }
  };
  const isDark = useSelector((state: RootState) => state.ui.theme) === 'dark';
  const [selectedJours, setSelectedJours] = useState<JourEmpruntable[]>([]);
  const [showContraintes, setShowContraintes] = useState(true);
  const [showEnseignants, setShowEnseignants] = useState(false);

  if (!validation && !isLoading) return null;

  const toggleJourEmprunt = (jour: JourEmpruntable) => {
    setSelectedJours(prev => {
      const exists = prev.some(j => j.date === jour.date);
      if (exists) return prev.filter(j => j.date !== jour.date);
      return [...prev, jour];
    });
  };

  const handleGenerate = () => {
    if (selectedJours.length > 0) {
      onGenerate(selectedJours.map(j => ({
        date: j.date,
        source: j.source,
        cible: j.cible,
      })));
    } else {
      onGenerate();
    }
  };

  const slotsEmpruntes = selectedJours.reduce((sum, j) => sum + j.slots, 0);

  // Count total alerts
  const nbEnsManquants = validation?.enseignants_manquants?.length || 0;
  const hasPerteJour1 = !!validation?.perte_jour1;
  const nbDureesPerso = validation?.durees_personnalisees?.length || 0;
  const totalAlertes = nbEnsManquants + (hasPerteJour1 ? 1 : 0) + nbDureesPerso;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto ${
        isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div>
            <h2 className="text-lg font-bold">Verification avant generation</h2>
            {validation && totalAlertes > 0 && (
              <p className="text-xs text-amber-500 mt-0.5">
                {totalAlertes} alerte(s) a verifier
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-blue-500" />
              <span className="ml-3">Analyse en cours...</span>
            </div>
          ) : validation ? (
            <div className="space-y-4">
              {/* ═══ Statut global ═══ */}
              <div className={`flex items-center gap-3 p-4 rounded-xl ${
                validation.peut_generer
                  ? isDark ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'
                  : isDark ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'
              }`}>
                {validation.peut_generer ? (
                  <CheckCircle size={24} className="text-green-500" />
                ) : (
                  <AlertTriangle size={24} className="text-amber-500" />
                )}
                <div>
                  <p className="font-semibold">
                    {validation.peut_generer
                      ? 'Tous les creneaux sont suffisants'
                      : 'Creneaux insuffisants pour certains niveaux'
                    }
                  </p>
                  <p className="text-sm text-gray-500">
                    {validation.jours_disponibles} jour(s) disponible(s), {validation.creneaux_par_jour} creneau(x)/jour
                  </p>
                </div>
              </div>

              {/* ═══ Analyse intelligente RATTRAPAGE ═══ */}
              {validation.analyse_rattrapage && (
                <div className={`rounded-lg border-2 p-4 ${
                  validation.analyse_rattrapage.mode === 'fusion'
                    ? (isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-300')
                    : (isDark ? 'bg-orange-900/20 border-orange-700' : 'bg-orange-50 border-orange-300')
                }`}>
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl">
                      {validation.analyse_rattrapage.mode === 'fusion' ? '🤝' : '🔀'}
                    </span>
                    <div className="flex-1">
                      <div className={`font-bold text-base ${
                        validation.analyse_rattrapage.mode === 'fusion'
                          ? 'text-green-800 dark:text-green-200'
                          : 'text-orange-800 dark:text-orange-200'
                      }`}>
                        Stratégie auto rattrapage : {validation.analyse_rattrapage.mode === 'fusion'
                          ? 'FUSION (tous les niveaux ensemble)'
                          : 'ALTERNANCE adaptée'}
                      </div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {validation.analyse_rattrapage.raison}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="text-[10px] text-gray-500 uppercase">À rattraper</div>
                      <div className="text-xl font-bold">
                        {validation.analyse_rattrapage.effectif_total_a_rattraper}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        L1: {validation.analyse_rattrapage.effectifs.L1} ·
                        L2: {validation.analyse_rattrapage.effectifs.L2} ·
                        L3: {validation.analyse_rattrapage.effectifs.L3}
                      </div>
                    </div>
                    <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="text-[10px] text-gray-500 uppercase">Capacité salles</div>
                      <div className="text-xl font-bold">
                        {validation.analyse_rattrapage.capacite_totale_salles}
                      </div>
                      <div className={`text-[10px] ${
                        validation.analyse_rattrapage.capacite_suffit_simultanement
                          ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {validation.analyse_rattrapage.capacite_suffit_simultanement
                          ? '✓ suffisante simultanément'
                          : '⚠ insuffisante'}
                      </div>
                    </div>
                    <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="text-[10px] text-gray-500 uppercase">Jours nécessaires</div>
                      <div className="text-xl font-bold">
                        {validation.analyse_rattrapage.mode === 'fusion'
                          ? validation.analyse_rattrapage.jours_requis_fusion
                          : validation.analyse_rattrapage.jours_requis_l1_seul + validation.analyse_rattrapage.jours_requis_l2l3_seul}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        L1: {validation.analyse_rattrapage.jours_requis_l1_seul}j ·
                        L2/L3: {validation.analyse_rattrapage.jours_requis_l2l3_seul}j
                      </div>
                    </div>
                    <div className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="text-[10px] text-gray-500 uppercase">Jours dispo</div>
                      <div className="text-xl font-bold">
                        {validation.analyse_rattrapage.jours_disponibles}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {validation.analyse_rattrapage.creneaux_par_jour} créneaux/jour
                      </div>
                    </div>
                  </div>

                  {validation.analyse_rattrapage.emprunts_auto !== undefined && validation.analyse_rattrapage.emprunts_auto > 0 && (
                    <div className="mt-3 p-2 rounded bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-xs text-yellow-800 dark:text-yellow-200">
                      🔁 <strong>Auto-emprunt</strong> : {validation.analyse_rattrapage.emprunts_auto} jour(s) partagé(s)
                      entre L1 et L2/L3 pour absorber la charge.
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="font-semibold mb-1">📅 Jours L1 planifiés ({validation.analyse_rattrapage.jours_l1_planifies?.length || 0})</div>
                      <div className="flex flex-wrap gap-1">
                        {(validation.analyse_rattrapage.jours_l1_planifies || []).map((d, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            {new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">📅 Jours L2/L3 planifiés ({validation.analyse_rattrapage.jours_l2l3_planifies?.length || 0})</div>
                      <div className="flex flex-wrap gap-1">
                        {(validation.analyse_rattrapage.jours_l2l3_planifies || []).map((d, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            {new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ Contraintes actives (éditable inline) ═══ */}
              <div className={`rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => setShowContraintes(!showContraintes)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm ${
                    isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                  } rounded-lg transition`}
                >
                  <span className="flex items-center gap-2">
                    <Settings size={16} className="text-gray-400" />
                    <span className="font-medium">Contraintes actives</span>
                    {contraintes && onApplyContraintes && (
                      <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>modifiable</span>
                    )}
                  </span>
                  {showContraintes ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showContraintes && (
                  <div className={`px-4 pb-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} space-y-4 pt-3 text-left`}>
                    {contraintes && onApplyContraintes ? (
                      <>
                        {/* 1. Niveaux à générer */}
                        <div>
                          <div className="text-xs font-medium mb-1.5">Niveaux à générer</div>
                          <div className="text-[11px] text-gray-500 mb-2">Vide = tous. Les autres niveaux conservent leurs créneaux existants.</div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {(['L1', 'L2', 'L3'] as const).map((niv) => {
                              const current = (contraintes.niveaux_filtre || []) as string[];
                              const active = current.includes(niv);
                              const disabled = applyingPayload !== null;
                              return (
                                <button
                                  key={niv}
                                  disabled={disabled}
                                  onClick={() => {
                                    const next = active ? current.filter((n) => n !== niv) : [...current, niv];
                                    handleApply({ niveaux_filtre: next });
                                  }}
                                  className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                    active
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {niv}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* 2. Pôles à générer */}
                        <div>
                          <div className="text-xs font-medium mb-1.5">Pôles à générer</div>
                          <div className="text-[11px] text-gray-500 mb-2">Vide = tous.</div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {([{ k: 'polytechnique', l: 'Polytechnique' }, { k: 'commerce_juridique', l: 'Commerce/Juridique' }] as const).map((p) => {
                              const current = (contraintes.poles_filtre || []) as string[];
                              const active = current.includes(p.k);
                              const disabled = applyingPayload !== null;
                              return (
                                <button
                                  key={p.k}
                                  disabled={disabled}
                                  onClick={() => {
                                    const next = active ? current.filter((x) => x !== p.k) : [...current, p.k];
                                    handleApply({ poles_filtre: next });
                                  }}
                                  className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                    active
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {p.l}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* 3. Alternance L1/L2-L3 (uniquement si pas de composition personnalisée) */}
                        {(!contraintes.composition_niveaux || contraintes.composition_niveaux.length === 0) && (
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-medium">Alternance L1 / L2-L3</div>
                              <div className="text-[11px] text-gray-500">L1 et L2-L3 composent des jours différents (réutilisation des salles).</div>
                            </div>
                            <button
                              disabled={applyingPayload !== null}
                              onClick={() => handleApply({ alternance_poles: !contraintes.alternance_poles })}
                              className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                contraintes.alternance_poles
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                              }`}
                            >
                              {contraintes.alternance_poles ? 'Activée' : 'Désactivée'}
                            </button>
                          </div>
                        )}

                        {/* 3-bis. Composition des groupes de niveaux (presets) */}
                        <div>
                          <div className="text-xs font-medium mb-1">Composition des groupes qui composent ensemble</div>
                          <div className="text-[11px] text-gray-500 mb-2">
                            Qui compose le même jour. Chaque groupe (séparé par |) prend un jour à tour de rôle.
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {([
                              { key: [] as string[][], label: 'Défaut (L1 | L2+L3)', hint: 'Comportement hérité' },
                              { key: [['L1'], ['L2', 'L3']], label: 'L1 | L2+L3', hint: '2 groupes : L1 seul et L2-L3 ensemble' },
                              { key: [['L1', 'L2'], ['L3']], label: 'L1+L2 | L3', hint: '2 groupes : L1-L2 ensemble et L3 seul' },
                              { key: [['L1', 'L3'], ['L2']], label: 'L1+L3 | L2', hint: '2 groupes : L1-L3 ensemble et L2 seul' },
                              { key: [['L1'], ['L2'], ['L3']], label: 'L1 | L2 | L3', hint: '3 groupes séparés (round-robin sur 3 jours)' },
                              { key: [['L1', 'L2', 'L3']], label: 'L1+L2+L3', hint: 'Tous ensemble chaque jour (pas d\'alternance niveaux)' },
                            ] as const).map((preset, idx) => {
                              const current = contraintes.composition_niveaux || [];
                              // Comparaison profonde simple : JSON stringify
                              const active = JSON.stringify(current) === JSON.stringify(preset.key);
                              return (
                                <button
                                  key={idx}
                                  disabled={applyingPayload !== null}
                                  title={preset.hint}
                                  onClick={() => handleApply({ composition_niveaux: preset.key })}
                                  className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                    active
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  } ${applyingPayload !== null ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {preset.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* 4. Alternance L2/L3 (au sein de leurs jours) */}
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium">Alternance L2 / L3 (1j/2)</div>
                            <div className="text-[11px] text-gray-500">L2 et L3 alternent au sein de leurs jours si places insuffisantes.</div>
                          </div>
                          <button
                            disabled={applyingPayload !== null}
                            onClick={() => handleApply({ alternance_l2_l3: !contraintes.alternance_l2_l3 })}
                            className={`px-2.5 py-1 text-xs rounded font-medium border ${
                              contraintes.alternance_l2_l3
                                ? 'bg-blue-600 text-white border-blue-600'
                                : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                            }`}
                          >
                            {contraintes.alternance_l2_l3 ? 'Activée' : 'Désactivée'}
                          </button>
                        </div>

                        {/* 5. Qui commence le jour 1 */}
                        {contraintes.alternance_poles && (
                          <div>
                            <div className="text-xs font-medium mb-1.5">Qui compose le jour 1</div>
                            <div className="flex items-center gap-1.5">
                              {(['L2L3', 'L1'] as const).map((g) => {
                                const current = (contraintes.groupe_premier_jour || 'L2L3') as string;
                                const active = current === g;
                                return (
                                  <button
                                    key={g}
                                    disabled={applyingPayload !== null}
                                    onClick={() => handleApply({ groupe_premier_jour: g })}
                                    className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                      active
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                    }`}
                                  >
                                    {g === 'L2L3' ? 'L2 et L3' : 'L1'}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* 6. Nombre de matières jour 1 */}
                        <div>
                          <div className="text-xs font-medium mb-1.5">Jour 1 : nombre de matières</div>
                          <div className="flex items-center gap-1.5">
                            {[1, 2, 3].map((n) => {
                              const current = (contraintes.nb_matieres_jour1 ?? (contraintes.jour1_une_seule_matiere ? 1 : 2)) as number;
                              const active = current === n;
                              return (
                                <button
                                  key={n}
                                  disabled={applyingPayload !== null}
                                  onClick={() => handleApply({ nb_matieres_jour1: n, jour1_une_seule_matiere: n === 1 })}
                                  className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                    active
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                  }`}
                                >
                                  {n} matière{n > 1 ? 's' : ''}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* 7. Samedis */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="text-xs font-medium">Examens le samedi</div>
                            <button
                              disabled={applyingPayload !== null}
                              onClick={() => handleApply({ composer_samedi: !contraintes.composer_samedi })}
                              className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                contraintes.composer_samedi
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                              }`}
                            >
                              {contraintes.composer_samedi ? 'Activé' : 'Désactivé'}
                            </button>
                          </div>
                          {contraintes.composer_samedi && (
                            <div className="flex items-center gap-1.5">
                              {[1, 2, 3].map((n) => {
                                const current = (contraintes.max_matieres_samedi ?? 2) as number;
                                const active = current === n;
                                return (
                                  <button
                                    key={n}
                                    disabled={applyingPayload !== null}
                                    onClick={() => handleApply({ max_matieres_samedi: n })}
                                    className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                      active
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                    }`}
                                  >
                                    Max {n}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* 7-bis. Durée de la pause entre matières */}
                        <div>
                          <div className="text-xs font-medium mb-1.5">Pause entre 2 matières (minutes)</div>
                          <div className="text-[11px] text-gray-500 mb-2">
                            Espace entre la fin d'une matière et le début de la suivante. Défaut 60 min.
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {[0, 15, 30, 45, 60, 90, 120].map((n) => {
                              const current = (contraintes.duree_pause_minutes ?? 60) as number;
                              const active = current === n;
                              return (
                                <button
                                  key={n}
                                  disabled={applyingPayload !== null}
                                  onClick={() => handleApply({ duree_pause_minutes: n })}
                                  className={`px-2.5 py-1 text-xs rounded font-medium border ${
                                    active
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                  }`}
                                >
                                  {n} min
                                </button>
                              );
                            })}
                          </div>
                          <div className="text-[11px] text-gray-500 mt-2">
                            Cette valeur est enregistrée. Elle sera utilisée à la prochaine génération.
                            Pour appliquer immédiatement à l'EDT existant, utilise le bouton
                            « Appliquer la pause » dans l'onglet Visualisation.
                          </div>
                        </div>

                        {/* 8. Répartition manuelle des jours */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="text-xs font-medium">Répartition manuelle des jours</div>
                            {contraintes.repartition_jours_manuelle && Object.keys(contraintes.repartition_jours_manuelle).length > 0 && (
                              <button
                                disabled={applyingPayload !== null}
                                onClick={() => handleApply({ repartition_jours_manuelle: {} })}
                                className="text-[11px] text-red-500 hover:underline"
                              >
                                Tout effacer
                              </button>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-500 mb-2">Force L1 / L2-L3 par jour. Les jours non listés utilisent l'alternance auto.</div>
                          <div className="space-y-1.5">
                            {Object.keys(contraintes.repartition_jours_manuelle || {}).sort().map((d) => {
                              const rep = (contraintes.repartition_jours_manuelle || {}) as Record<string, string[]>;
                              const grp = rep[d] || [];
                              const isL1 = grp.includes('L1');
                              const isL2L3 = grp.includes('L2') || grp.includes('L3');
                              return (
                                <div key={d} className="flex items-center gap-1.5">
                                  <input
                                    type="date"
                                    value={d}
                                    min={dateDebut}
                                    max={dateFin}
                                    disabled={applyingPayload !== null}
                                    onChange={(e) => {
                                      const nd = e.target.value;
                                      if (!nd || nd === d) return;
                                      const next = { ...rep };
                                      delete next[d];
                                      next[nd] = grp;
                                      handleApply({ repartition_jours_manuelle: next });
                                    }}
                                    className={`px-1.5 py-0.5 text-[11px] rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300'}`}
                                  />
                                  <button
                                    disabled={applyingPayload !== null}
                                    onClick={() => {
                                      const next = { ...rep };
                                      const g = new Set(grp);
                                      if (isL1) g.delete('L1'); else g.add('L1');
                                      const arr = Array.from(g);
                                      if (arr.length === 0) delete next[d]; else next[d] = arr;
                                      handleApply({ repartition_jours_manuelle: next });
                                    }}
                                    className={`px-2 py-0.5 text-[11px] rounded font-medium border ${
                                      isL1
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                    }`}
                                  >L1</button>
                                  <button
                                    disabled={applyingPayload !== null}
                                    onClick={() => {
                                      const next = { ...rep };
                                      const g = new Set(grp);
                                      if (isL2L3) { g.delete('L2'); g.delete('L3'); } else { g.add('L2'); g.add('L3'); }
                                      const arr = Array.from(g);
                                      if (arr.length === 0) delete next[d]; else next[d] = arr;
                                      handleApply({ repartition_jours_manuelle: next });
                                    }}
                                    className={`px-2 py-0.5 text-[11px] rounded font-medium border ${
                                      isL2L3
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                                    }`}
                                  >L2/L3</button>
                                  <button
                                    disabled={applyingPayload !== null}
                                    onClick={() => {
                                      const next = { ...rep };
                                      delete next[d];
                                      handleApply({ repartition_jours_manuelle: next });
                                    }}
                                    className="ml-auto text-[11px] text-red-500 hover:underline"
                                  >Supprimer</button>
                                </div>
                              );
                            })}
                            <button
                              disabled={applyingPayload !== null}
                              onClick={() => {
                                const rep = (contraintes.repartition_jours_manuelle || {}) as Record<string, string[]>;
                                const anchor = dateDebut && !rep[dateDebut] ? dateDebut : (dateFin || dateDebut || '');
                                if (!anchor || rep[anchor]) return;
                                handleApply({ repartition_jours_manuelle: { ...rep, [anchor]: ['L2', 'L3'] } });
                              }}
                              className={`px-2 py-1 text-[11px] rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                              + Ajouter un jour
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* Fallback : lecture seule si les props ne sont pas fournies */
                      <ul className="space-y-1">
                        {(validation.contraintes_actives || []).map((c, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs">
                            <span className="text-blue-500 mt-0.5">•</span>
                            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{c}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* ═══ Perte de créneaux Jour 1 ═══ */}
              {validation.perte_jour1 && (
                <div className={`flex items-start gap-3 p-3 rounded-lg ${
                  isDark ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'
                }`}>
                  <Clock size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">
                      Jour 1 ({validation.perte_jour1.jour_semaine} {validation.perte_jour1.date_label}) :
                      {' '}{validation.perte_jour1.slots_jour1} creneau(x) au lieu de {validation.perte_jour1.slots_normal}
                    </p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {validation.perte_jour1.perte} creneau(x) perdu(s) a cause des regles du premier jour
                    </p>
                  </div>
                </div>
              )}

              {/* ═══ Durées personnalisées ═══ */}
              {validation.durees_personnalisees && validation.durees_personnalisees.length > 0 && (
                <div className={`flex items-start gap-3 p-3 rounded-lg ${
                  isDark ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'
                }`}>
                  <Info size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Durees personnalisees :</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {validation.durees_personnalisees.map((d, i) => (
                        <span key={i} className={`text-xs px-2 py-0.5 rounded ${
                          isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {d.matiere} → {d.duree}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ Enseignants manquants ═══ */}
              {nbEnsManquants > 0 && (
                <div className={`rounded-lg border ${
                  isDark ? 'border-amber-700 bg-amber-900/10' : 'border-amber-200 bg-amber-50'
                }`}>
                  <button
                    onClick={() => setShowEnseignants(!showEnseignants)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm rounded-lg`}
                  >
                    <span className="flex items-center gap-2">
                      <UserX size={16} className="text-amber-500" />
                      <span className="font-medium text-amber-600">
                        {nbEnsManquants} matiere(s) sans formateur attribue
                      </span>
                    </span>
                    {showEnseignants ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {showEnseignants && validation.enseignants_manquants && (
                    <div className={`px-4 pb-3 border-t ${isDark ? 'border-amber-800' : 'border-amber-200'}`}>
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {validation.enseignants_manquants.map((e, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className={`px-1.5 py-0.5 rounded font-medium ${
                              isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                            }`}>{e.niveau}</span>
                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{e.matiere}</span>
                            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({e.option})</span>
                          </div>
                        ))}
                      </div>
                      <p className={`text-xs mt-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                        La generation fonctionnera, mais l'emploi du temps affichera des cases sans nom de formateur.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ Répartition des jours (alternance) ═══ */}
              {validation.repartition_jours?.alternance_active && (
                <div className={`rounded-lg p-4 border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <ArrowLeftRight size={16} className="text-purple-500" />
                    Repartition des jours (alternance)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={`text-xs font-medium mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        L2/L3 — {validation.repartition_jours.total_slots_l2_l3} creneau(x)
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {validation.repartition_jours.l2_l3.map((j, i) => (
                          <span key={i} className={`text-xs px-2 py-1 rounded ${
                            j.est_premier
                              ? isDark ? 'bg-blue-800/50 text-blue-200 border border-blue-600' : 'bg-blue-200 text-blue-800 border border-blue-300'
                              : isDark ? 'bg-blue-900/30 text-blue-300 border border-blue-800' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {j.jour_semaine} {j.date_label.slice(0, 5)}
                            <span className="ml-1 opacity-60">({j.slots})</span>
                            {j.est_premier && <span className="ml-1 text-amber-400">J1</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className={`text-xs font-medium mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        L1 — {validation.repartition_jours.total_slots_l1} creneau(x)
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {validation.repartition_jours.l1.map((j, i) => (
                          <span key={i} className={`text-xs px-2 py-1 rounded ${
                            j.est_premier
                              ? isDark ? 'bg-emerald-800/50 text-emerald-200 border border-emerald-600' : 'bg-emerald-200 text-emerald-800 border border-emerald-300'
                              : isDark ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-800' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {j.jour_semaine} {j.date_label.slice(0, 5)}
                            <span className="ml-1 opacity-60">({j.slots})</span>
                            {j.est_premier && <span className="ml-1 text-amber-400">J1</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ Tableau d'analyse par niveau ═══ */}
              <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <table className="w-full text-sm">
                  <thead className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
                    <tr>
                      <th className="text-left px-4 py-2">Niveau</th>
                      <th className="text-center px-4 py-2">Matieres</th>
                      <th className="text-center px-4 py-2">Creneaux dispo.</th>
                      <th className="text-center px-4 py-2">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(validation.analyse).map(([niveau, data]) => (
                      <tr key={niveau} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${
                        data.deficit > 0 ? isDark ? 'bg-red-900/10' : 'bg-red-50' : ''
                      }`}>
                        <td className="px-4 py-3 font-medium">{niveau}</td>
                        <td className="text-center px-4 py-3">{data.matieres_total}</td>
                        <td className="text-center px-4 py-3">
                          {data.slots_disponibles}
                          {selectedJours.length > 0 && (
                            niveau !== selectedJours[0].source
                              ? <span className="text-green-500 ml-1">(+{slotsEmpruntes})</span>
                              : null
                          )}
                        </td>
                        <td className="text-center px-4 py-3">
                          {data.deficit > 0 ? (
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              isDark ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-700'
                            }`}>
                              -{data.deficit} creneau(x)
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              isDark ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-700'
                            }`}>
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Groupes avec le plus de matières */}
              {!validation.peut_generer && (
                <div className="space-y-2">
                  {Object.entries(validation.analyse)
                    .filter(([, data]) => data.deficit > 0)
                    .map(([niveau, data]) => (
                      <div key={niveau}>
                        <p className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Groupes {niveau} ayant le plus de matieres :
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[...data.groupes]
                            .sort((a, b) => b.nb_matieres - a.nb_matieres)
                            .slice(0, 5)
                            .map((g, i) => (
                              <span key={i} className={`text-xs px-2 py-1 rounded ${
                                isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {g.label} ({g.nb_matieres} mat.)
                              </span>
                            ))
                          }
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}

              {/* ═══ Suggestions ═══ */}
              {validation.suggestions.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Solutions :</h4>
                  {validation.suggestions.map((s, i) => (
                    <div key={i}>
                      {/* Emprunter un jour */}
                      {s.type === 'emprunter_jour' && s.jours_empruntables ? (
                        <div className={`p-4 rounded-lg ${
                          isDark ? 'bg-purple-900/20 border border-purple-700' : 'bg-purple-50 border border-purple-200'
                        }`}>
                          <div className="flex items-start gap-3 mb-3">
                            <ArrowLeftRight size={18} className="text-purple-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{s.message}</p>
                              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Selectionnez le(s) jour(s) a emprunter de {s.source_groupe} pour {s.cible_groupe}
                                {' '}(le {s.source_groupe} garde ses jours normalement) :
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {s.jours_empruntables.map((jour, j) => {
                              const isSelected = selectedJours.some(sj => sj.date === jour.date);
                              const charge = jour.charge;
                              const chargeColor = charge?.label === 'leger'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : charge?.label === 'moyen'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : charge?.label === 'sature'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : '';
                              const chargeIcon = charge?.label === 'leger' ? '🟢'
                                : charge?.label === 'moyen' ? '🟡'
                                : charge?.label === 'sature' ? '🔴' : '';
                              return (
                                <button
                                  key={j}
                                  onClick={() => toggleJourEmprunt(jour)}
                                  title={charge ? `Charge prévue : ${charge.effectif}/${charge.capacite} étudiants (${charge.taux}%) — marge ${charge.marge} places` : undefined}
                                  className={`flex flex-col items-stretch gap-1 px-3 py-2 rounded-lg text-sm border transition ${
                                    isSelected
                                      ? 'bg-purple-600 text-white border-purple-600'
                                      : isDark
                                        ? 'bg-gray-800 text-gray-300 border-gray-600 hover:border-purple-500'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    {isSelected && <Check size={14} />}
                                    <span className="font-medium">{jour.jour_semaine}</span>
                                    <span className="opacity-70">{jour.date_label}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      isSelected
                                        ? 'bg-purple-500'
                                        : isDark ? 'bg-gray-700' : 'bg-gray-100'
                                    }`}>
                                      {jour.slots} cr.
                                    </span>
                                  </div>
                                  {charge && (
                                    <div className={`text-[10px] px-1.5 py-0.5 rounded text-center ${
                                      isSelected ? 'bg-purple-700' : chargeColor
                                    }`}>
                                      {chargeIcon} {charge.effectif} étud. attendus ({charge.taux}%) · marge +{charge.marge}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          {selectedJours.length > 0 && (
                            <div className={`mt-3 p-2 rounded text-xs ${
                              isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {selectedJours.length} jour(s) selectionne(s) — +{slotsEmpruntes} creneau(x)
                              {' '}pour {s.cible_groupe} (ajout sans retirer du {s.source_groupe})
                            </div>
                          )}
                        </div>
                      ) : s.type !== 'detail_niveau' ? (
                        <div className={`flex items-start gap-3 p-3 rounded-lg ${
                          s.priorite === 'haute'
                            ? (isDark ? 'bg-orange-900/20 border-2 border-orange-600' : 'bg-orange-50 border-2 border-orange-400')
                            : isDark ? 'bg-gray-800 border border-gray-700' : 'bg-blue-50 border border-blue-200'
                        }`}>
                          <span className="text-xl flex-shrink-0 mt-0.5">
                            {s.priorite === 'haute' ? '⚡' : <Calendar size={18} className="text-blue-500" />}
                          </span>
                          <div className="flex-1">
                            <div className="text-sm">{s.message}</div>
                          </div>
                          {s.type === 'etendre_periode' && s.nouvelle_date_fin && (
                            <button
                              onClick={() => onExtendPeriod(s.nouvelle_date_fin!)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition whitespace-nowrap"
                            >
                              Appliquer <ArrowRight size={14} />
                            </button>
                          )}
                          {s.action_payload && onApplyContraintes && (
                            <button
                              onClick={() => handleApply(s.action_payload!)}
                              disabled={applyingPayload !== null}
                              className={`flex items-center gap-1 px-3 py-1.5 text-white text-sm rounded-lg transition whitespace-nowrap ${
                                s.priorite === 'haute'
                                  ? 'bg-orange-600 hover:bg-orange-700'
                                  : 'bg-blue-600 hover:bg-blue-700'
                              } disabled:opacity-50`}
                            >
                              {applyingPayload === s.action_payload ? '...' : (s.action_label || 'Appliquer')}
                              {applyingPayload !== s.action_payload && <ArrowRight size={14} />}
                            </button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-6 py-4 border-t ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="text-xs text-gray-500">
            {validation && nbEnsManquants > 0 && (
              <span className="text-amber-500">{nbEnsManquants} formateur(s) manquant(s)</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm ${
                isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
              } transition`}
            >
              Annuler
            </button>
            {validation && (
              <button
                onClick={handleGenerate}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition ${
                  validation.peut_generer || selectedJours.length > 0
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                {validation.peut_generer
                  ? 'Generer'
                  : selectedJours.length > 0
                    ? `Generer avec ${selectedJours.length} jour(s) emprunte(s)`
                    : 'Generer quand meme'
                }
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreValidationModal;
